import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MaintenanceWizard from "@/components/MaintenanceWizard";
import { Wrench, Search, ChevronRight, RefreshCw, Plus } from "lucide-react";

// P0.3.1 — Vue globale Maintenance (basée sur GET /maintenance/overview, RBAC backend, 2 requêtes, sans polling).
const TABS = [
  { key: "all", label: "Toutes", states: null },
  { key: "planned", label: "À venir", states: ["planned"] },
  { key: "soon", label: "À prévoir", states: ["soon"] },
  { key: "due", label: "À échéance", states: ["due"] },
  { key: "overdue", label: "En retard", states: ["overdue"] },
];

// Style/état visuel par état métier.
const STATE_META = {
  planned: { label: "À venir", color: "#64748B", bg: "#64748B22", border: "#64748B55" },
  soon: { label: "À prévoir", color: "#F59E0B", bg: "#F59E0B22", border: "#F59E0B66" },
  due: { label: "À échéance", color: "#EF4444", bg: "#EF444422", border: "#EF444466" },
  overdue: { label: "En retard", color: "#B91C1C", bg: "#B91C1C33", border: "#B91C1C88" },
};

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-FR"));
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return d; } };

const dueLabel = (r) => (r.due != null ? `${fmtNum(r.due)} ${r.unit}` : (r.due_date ? fmtDate(r.due_date) : "—"));
const currentLabel = (r) => `${fmtNum(r.current_value)} ${r.unit}`;
const remainingLabel = (r) => {
  if (r.remaining == null) return "—";
  if (r.remaining < 0) return `Retard ${fmtNum(Math.abs(r.remaining))} ${r.unit}`;
  return `${fmtNum(r.remaining)} ${r.unit} restants`;
};

function StatusPill({ state }) {
  const m = STATE_META[state] || STATE_META.planned;
  return (
    <span className="text-[11px] font-bold px-2 py-0.5 rounded whitespace-nowrap"
      style={{ color: m.color, background: m.bg, border: `1px solid ${m.border}` }} data-testid={`maint-status-${state}`}>
      {m.label}
    </span>
  );
}

export default function MaintenancePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user && ["admin", "gestionnaire"].includes(user.role);
  const [maintWizard, setMaintWizard] = useState(false);
  const [rows, setRows] = useState([]);
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("");
  const [poolFilter, setPoolFilter] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pl] = await Promise.all([
        api.get("/maintenance/overview").then((r) => r.data).catch(() => []),
        api.get("/pools").then((r) => r.data).catch(() => []),
      ]);
      setRows(ov || []);
      setPools(pl || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); /* une seule passe, aucun polling */ }, [load]);

  // Société : dérivée des lignes (aucun concept supplémentaire).
  const companies = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.company_id) m.set(r.company_id, r.company_name || "—"); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Groupe : appartenance véhicule → pool (1 requête /pools).
  const poolVehicleIds = useMemo(() => {
    const p = pools.find((x) => x.id === poolFilter);
    return p ? new Set(p.vehicle_ids || []) : null;
  }, [pools, poolFilter]);

  // Lignes filtrées par société / groupe / recherche (SANS le filtre d'onglet) — base du mini-résumé et de la liste.
  const scopedRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (companyFilter && r.company_id !== companyFilter) return false;
      if (poolVehicleIds && !poolVehicleIds.has(r.vehicle_id)) return false;
      if (needle && !(`${r.vehicle_name} ${r.plate || ""} ${r.label || ""}`.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [rows, companyFilter, poolVehicleIds, q]);

  const counts = useMemo(() => {
    const c = { all: scopedRows.length, planned: 0, soon: 0, due: 0, overdue: 0 };
    scopedRows.forEach((r) => { if (c[r.state] != null) c[r.state] += 1; });
    return c;
  }, [scopedRows]);

  const filtered = useMemo(() => {
    const tabDef = TABS.find((t) => t.key === tab);
    return scopedRows.filter((r) => !tabDef?.states || tabDef.states.includes(r.state));
  }, [scopedRows, tab]);

  const openVehicle = (r) => navigate(`/dashboard?vehicle=${encodeURIComponent(r.vehicle_id)}&tab=maintenance`);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <p className="label-mono">Suivi flotte</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-3"><Wrench className="w-7 h-7 brand-orange" /> Maintenance</h1>
        </div>
        <button onClick={load} data-testid="maint-refresh" className="flex items-center gap-2 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-4 py-2.5 rounded-md text-sm transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
        </button>
      </div>
      {canManage && <button onClick={() => setMaintWizard(true)} data-testid="maint-plan-btn-global" className="flex items-center gap-2 bg-[#F5821F] hover:bg-[#e0741a] text-white font-bold px-4 py-2.5 rounded-md text-sm mb-4"><Plus className="w-4 h-4" /> Planifier un entretien</button>}
      <MaintenanceWizard open={maintWizard} onClose={() => setMaintWizard(false)} onCreated={load} />

      {/* Mini-résumé : 4 indicateurs cliquables (données déjà chargées, aucune requête) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="maint-summary">
        {[
          { key: "planned", label: "À venir", n: counts.planned, color: "#64748B", bg: "#64748B14", border: "#64748B44" },
          { key: "soon", label: "À prévoir", n: counts.soon, color: "#F59E0B", bg: "#F59E0B14", border: "#F59E0B55" },
          { key: "due", label: "À échéance", n: counts.due, color: "#EF4444", bg: "#EF444414", border: "#EF444455" },
          { key: "overdue", label: "En retard", n: counts.overdue, color: "#B91C1C", bg: "#B91C1C1f", border: "#B91C1C77" },
        ].map((s) => (
          <button key={s.key} onClick={() => setTab(s.key)} data-testid={`maint-summary-${s.key}`}
            className={`flex items-center justify-between rounded-lg border px-4 py-3 transition-all hover:shadow-sm ${tab === s.key ? "ring-2" : ""}`}
            style={{ background: s.bg, borderColor: s.border, boxShadow: tab === s.key ? `0 0 0 2px ${s.border}` : undefined }}>
            <span className="text-sm font-semibold" style={{ color: s.color }}>{s.label}</span>
            <span className="text-2xl font-extrabold tabular-nums" style={{ color: s.color }} data-testid={`maint-summary-${s.key}-count`}>{s.n}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-3" data-testid="maint-total">Total surveillé : <span className="font-bold text-foreground">{counts.all}</span></p>

      {/* Onglets état */}
      <div className="flex items-center gap-1 mb-4 border-b border-border overflow-x-auto no-scrollbar" data-testid="maint-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`maint-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap flex items-center gap-2 ${tab === t.key ? "border-[#17A2B8] text-[#17A2B8]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
            <span className="data text-[11px] px-1.5 py-0.5 rounded bg-secondary">{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="maint-filters">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="maint-search" placeholder="Rechercher un véhicule / machine…"
            className="bg-secondary/60 border border-border rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-[#17A2B8] min-w-[220px]" />
        </div>
        {companies.length > 1 && (
          <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} data-testid="maint-filter-company"
            className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-sm outline-none focus:border-[#17A2B8]">
            <option value="">Toutes les sociétés</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {pools.length > 0 && (
          <select value={poolFilter} onChange={(e) => setPoolFilter(e.target.value)} data-testid="maint-filter-pool"
            className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-sm outline-none focus:border-[#17A2B8]">
            <option value="">Tous les groupes</option>
            {pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {(q || companyFilter || poolFilter) && (
          <button onClick={() => { setQ(""); setCompanyFilter(""); setPoolFilter(""); }} data-testid="maint-filter-reset" className="text-xs text-muted-foreground hover:text-foreground px-1">Réinitialiser</button>
        )}
      </div>

      {/* Desktop : tableau */}
      <div className="hidden md:block border border-border rounded-lg overflow-hidden bg-card" data-testid="maint-table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40"><tr className="label-mono text-left">
            <th className="px-4 py-3">Société</th>
            <th className="px-4 py-3">Véhicule / Machine</th>
            <th className="px-4 py-3">Type d'entretien</th>
            <th className="px-4 py-3">Échéance</th>
            <th className="px-4 py-3">Compteur actuel</th>
            <th className="px-4 py-3">Restant / retard</th>
            <th className="px-4 py-3">Statut</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody className="data">
            {loading && <tr><td colSpan={8} className="text-center text-muted-foreground py-10" data-testid="maint-loading">Chargement…</td></tr>}
            {!loading && filtered.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-black/5 cursor-pointer" data-testid={`maint-row-${r.id}`} onClick={() => openVehicle(r)}>
                <td className="px-4 py-3 text-muted-foreground">{r.company_name || "—"}</td>
                <td className="px-4 py-3 font-semibold">{r.vehicle_name}{r.plate ? <span className="text-muted-foreground font-normal"> · {r.plate}</span> : null}</td>
                <td className="px-4 py-3">{r.label}</td>
                <td className="px-4 py-3">{dueLabel(r)}</td>
                <td className="px-4 py-3 tabular-nums">{currentLabel(r)}</td>
                <td className="px-4 py-3 tabular-nums" style={r.remaining != null && r.remaining < 0 ? { color: "#B91C1C", fontWeight: 700 } : undefined}>{remainingLabel(r)}</td>
                <td className="px-4 py-3"><StatusPill state={r.state} /></td>
                <td className="px-4 py-3 text-right"><button data-testid={`maint-open-${r.id}`} onClick={(e) => { e.stopPropagation(); openVehicle(r); }} className="text-[#17A2B8] hover:underline text-xs font-semibold flex items-center gap-1 ml-auto">Voir véhicule <ChevronRight className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-10" data-testid="maint-empty">Aucune échéance de maintenance pour ce filtre.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile : cartes */}
      <div className="md:hidden space-y-3" data-testid="maint-cards">
        {loading && <p className="text-center text-muted-foreground py-8">Chargement…</p>}
        {!loading && filtered.map((r) => (
          <button key={r.id} onClick={() => openVehicle(r)} data-testid={`maint-card-${r.id}`}
            className="w-full text-left border border-border rounded-lg bg-card p-4 active:bg-black/5 transition-colors">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="min-w-0">
                <p className="font-bold truncate">{r.vehicle_name}{r.plate ? <span className="text-muted-foreground font-normal"> · {r.plate}</span> : null}</p>
                <p className="text-xs text-muted-foreground truncate">{r.company_name || ""} · {r.label}</p>
              </div>
              <StatusPill state={r.state} />
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground text-xs">Échéance : <span className="text-foreground">{dueLabel(r)}</span></span>
              <span className="tabular-nums text-xs" style={r.remaining != null && r.remaining < 0 ? { color: "#B91C1C", fontWeight: 700 } : undefined}>{remainingLabel(r)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 tabular-nums">Compteur : {currentLabel(r)}</p>
          </button>
        ))}
        {!loading && filtered.length === 0 && <p className="text-center text-muted-foreground py-8" data-testid="maint-empty-m">Aucune échéance de maintenance pour ce filtre.</p>}
      </div>
    </Layout>
  );
}
